import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { listCustomers, patchCustomer, type Customer, type PatchCustomerBody } from "../api/portal";

type Pending =
  | { kind: "deactivate" | "activate" | "api_enable" | "api_disable"; customer: Customer }
  | null;

export function CustomersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [pending, setPending] = useState<Pending>(null);
  const [revoke, setRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCreds, setNewCreds] = useState<Record<string, string> | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["customers"], queryFn: listCustomers });

  const toggle = useMutation({
    mutationFn: ({ clientId, body }: { clientId: string; body: PatchCustomerBody }) =>
      patchCustomer(clientId, body),
    onSuccess: (result) => {
      setError(null);
      setPending(null);
      setRevoke(false);
      // A re-enable after a revoke mints NEW credentials the customer has to
      // be given -- surface them once rather than silently dropping them.
      setNewCreds(
        result.applied.api_credentials?.client_secret
          ? (result.applied.api_credentials as Record<string, string>)
          : null,
      );
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? `Could not apply the change (${e.status}).`
                                     : "Could not apply the change.");
    },
  });

  const customers = data?.customers ?? [];
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) => c.client_name.toLowerCase().includes(q) ||
             c.client_id.toLowerCase().includes(q) ||
             (c.admin_email ?? "").toLowerCase().includes(q),
    );
  }, [customers, filter]);

  const confirmDialog = () => {
    if (!pending) return null;
    const { kind, customer } = pending;

    if (kind === "deactivate") {
      return (
        <ConfirmDialog
          title={`Deactivate ${customer.client_name}?`}
          tone="danger"
          confirmLabel="Deactivate customer"
          busy={toggle.isPending}
          consequences={[
            "Everyone at this customer is locked out of the portal immediately.",
            "Their ingestion API starts returning 403 — a live integration will break.",
            "Their data is untouched, and SFTP uploads still process as normal.",
            "You can reactivate at any time; nothing is destroyed.",
          ]}
          onCancel={() => setPending(null)}
          onConfirm={() => toggle.mutate({ clientId: customer.client_id, body: { active: false } })}
        />
      );
    }
    if (kind === "activate") {
      return (
        <ConfirmDialog
          title={`Reactivate ${customer.client_name}?`}
          confirmLabel="Reactivate customer"
          busy={toggle.isPending}
          consequences={[
            "Their users can log in to the portal again.",
            "Their ingestion API resumes accepting transactions if the API is enabled.",
          ]}
          onCancel={() => setPending(null)}
          onConfirm={() => toggle.mutate({ clientId: customer.client_id, body: { active: true } })}
        />
      );
    }
    if (kind === "api_enable") {
      return (
        <ConfirmDialog
          title={`Enable the API for ${customer.client_name}?`}
          confirmLabel="Enable API access"
          busy={toggle.isPending}
          consequences={[
            "They can submit transactions to POST /v1/transactions.",
            "If their credentials were previously revoked, new ones are issued now and shown once.",
          ]}
          onCancel={() => setPending(null)}
          onConfirm={() => toggle.mutate({ clientId: customer.client_id, body: { api_enabled: true } })}
        />
      );
    }
    return (
      <ConfirmDialog
        title={`Disable the API for ${customer.client_name}?`}
        tone="danger"
        confirmLabel="Disable API access"
        busy={toggle.isPending}
        consequences={[
          "Their ingestion API starts returning 403 immediately.",
          "Portal access is unaffected — this is the API switch only.",
          revoke
            ? "Their credentials are DELETED. Re-enabling issues new ones that you must send them."
            : "Their credentials are kept, so re-enabling is instant.",
        ]}
        extra={
          <label className="checkbox-row">
            <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} />
            Also revoke their credentials (they cannot self-recover from this)
          </label>
        }
        onCancel={() => { setPending(null); setRevoke(false); }}
        onConfirm={() =>
          toggle.mutate({ clientId: customer.client_id, body: { api_enabled: false, revoke } })}
      />
    );
  };

  return (
    <div>
      <div className="page-header">
        <h2>Customers</h2>
        <Link className="btn-primary" to="/onboard">Onboard customer</Link>
      </div>

      <input
        className="search"
        placeholder="Search by name, id, or admin email…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {error && <p style={{ color: "var(--red)" }}>{error}</p>}
      {newCreds && (
        <div className="card creds-callout">
          <strong>New API credentials — shown once.</strong>
          <dl>
            {Object.entries(newCreds).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd className="mono">{v}</dd></div>
            ))}
          </dl>
          <button onClick={() => setNewCreds(null)}>Dismiss</button>
        </div>
      )}

      {isLoading && <p>Loading…</p>}
      {!isLoading && shown.length === 0 && (
        <p className="empty">{customers.length === 0 ? "No customers yet." : "No matching customers."}</p>
      )}

      {shown.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th><th>Status</th><th>API</th><th>Reports</th>
              <th>Customer Admin</th><th>Onboarded</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.client_id} className="clickable"
                  onClick={() => navigate(`/customers/${c.client_id}/`)}>
                <td>
                  <div>{c.client_name}</div>
                  <div className="mono dim">{c.client_id}</div>
                </td>
                <td>
                  <span className={`badge ${c.active ? "ok" : "inactive"}`}>
                    {c.active ? "active" : "inactive"}
                  </span>
                </td>
                <td>
                  <span className={`badge ${c.api_enabled ? "ok" : "off"}`}>
                    {c.api_enabled ? "on" : "off"}
                  </span>
                </td>
                <td className="mono">{c.report_types.join(", ")}</td>
                <td>{c.admin_email ?? <span className="dim">none</span>}</td>
                <td>{new Date(c.created_at).toLocaleDateString()}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {/* Explicit affordance: clicking the row switches customer
                      context and lands on the dashboard, so without this the
                      settings page is only reachable by noticing a new nav
                      item appear afterwards. */}
                  <Link className="btn-link" to={`/customers/${c.client_id}/settings`}>
                    Settings
                  </Link>
                  <button onClick={() =>
                    setPending({ kind: c.active ? "deactivate" : "activate", customer: c })}>
                    {c.active ? "Deactivate" : "Reactivate"}
                  </button>
                  <button onClick={() =>
                    setPending({ kind: c.api_enabled ? "api_disable" : "api_enable", customer: c })}>
                    {c.api_enabled ? "Disable API" : "Enable API"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDialog()}
    </div>
  );
}
